import React from 'react'
import { Box, ButtonBase, Tooltip } from '@mui/material'

import { dsColorNew } from '@palette'
import { money, signedMoney } from '../../../@services/format'
import { marketTone, pct, points, tone } from '../@services/colourScale'


// green = you are cheaper (or your distance fell), red = dearer; three intensities, none inside ±2 % (CA 13)
const BG = { cheaper: 'rgba(22,163,74,', dearer: 'rgba(220,38,38,' }
const ALPHA = [0, 0.09, 0.2, 0.34]
const INK = { cheaper: '#166534', dearer: '#B91C1C', inline: dsColorNew.textMuted }

export const cellBg = t => (t && t.level ? `${BG[t.side]}${ALPHA[t.level]})` : 'transparent')
export const cellInk = t => (t ? INK[t.side] : dsColorNew.textSubtle)

export const REASON_TEXT = {
	noPrices: ['No prices', 'no price'],
	noYou: ['No prices of your brand', 'no you'],
	noYouSite: ['No prices of your website', 'no you'],
	fewCompetitors: ['Not enough competitors', '<2 comp.'],
}

function reasonOf(cell, mode) {
	if (cell.reason === 'noYou' && mode === 'site') return 'noYouSite'
	return cell.reason
}

/**
 * One cell of the network (CU-869fmjzrh D10): your distance to the median (sign, arrow, %, € and, with Show change, its
 * change in points), coloured; without identity the median and its change. An empty cell says why (CA 9); few
 * prices behind it: "!" and dimmed (CA 10). A button: Enter or a click opens the detail.
 */
export default function DistanceCell({ cell, mode, code, compact = false, showChange = false, perDay = false, aria, onOpen }) {
	const reason = reasonOf(cell, mode)
	if (reason && !(mode === 'market' && cell.median !== null)) {
		const [long, short] = REASON_TEXT[reason] || ['—', '—']
		const title = reason === 'fewCompetitors' ? `Not enough competitors: ${cell.nCompetitors} with prices, at least 2 needed for a median` : long
		return <Box title={title} aria-label={`${aria}: ${long}`} sx={{ minWidth: compact ? 58 : 96, minHeight: 44, px: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: dsColorNew.textSubtle, textAlign: 'center', lineHeight: 1.2 }}>
			{compact ? short : long}
		</Box>
	}

	const market = mode === 'market'
	const t = market ? marketTone(cell.marketChange) : tone(cell.distance)
	const ratio = market ? cell.marketChange : cell.distance
	const top = market ? money(cell.median, code) : `${t?.arrow || ''} ${pct(ratio)}`
	const sub = market
		? (cell.marketChange === null ? 'no comparison' : `${t.arrow} ${pct(cell.marketChange)}`)
		: compact ? null : `${signedMoney(cell.distanceE, code)}${perDay ? '/day' : ''}`
	const change = !market && showChange && cell.change !== null ? `Δ ${points(cell.change)}` : null
	const label = `${aria}: ${market ? `market median ${money(cell.median, code)}` : `${pct(cell.distance)} vs the market median`}${cell.thin ? ', few prices' : ''}. Open details`

	const button = <ButtonBase aria-label={label} onClick={onOpen} sx={{
		width: '100%', minWidth: compact ? 58 : 96, minHeight: 44, px: 0.5, py: 0.5, borderRadius: '8px', position: 'relative',
		display: 'flex', flexDirection: 'column', gap: '1px', backgroundColor: market ? 'transparent' : cellBg(t), opacity: cell.thin ? 0.55 : 1,
		boxShadow: !market && cell.position === 1 && cell.total > 1 ? `inset 0 0 0 2px ${dsColorNew.successText}` : 'none',
		'&:hover': { outline: `1px solid ${dsColorNew.appBlue}` }, '&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.appBlue}` },
	}}>
		<Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: market ? dsColorNew.textTitle : cellInk(t), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{top}</Box>
		{!!sub && <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: market ? cellInk(t) : dsColorNew.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{sub}</Box>}
		{!!change && <Box component="span" sx={{ fontSize: 10.5, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>{change}</Box>}
		{cell.thin && <Box component="span" sx={{ position: 'absolute', top: 2, right: 4, fontSize: 11, fontWeight: 700, color: dsColorNew.noteAmber }}>!</Box>}
	</ButtonBase>
	if (!cell.thin) return button
	const yours = cell.you?.n || 0
	return <Tooltip title={`Few prices behind this cell: ${Math.max(cell.n - yours, 0)} of the market, ${yours} yours`}>{button}</Tooltip>
}
